import { BookmarkType } from "./bookmarks-config";

const basePath = "/ajax/bookmarks";

export const add = async (type: string, id: string | number, access_token: string): Promise<BookmarkAddResponse> => {
  const typeIdKey = type + "Id";
  const requestPayload = {
    type: type,
    [typeIdKey]: id
  };

  const response = await fetch(`${basePath}/add`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${access_token}`
    },
    body: JSON.stringify(requestPayload)
  });

  if (!response.ok) {
    throw new Error("Failed to add bookmark");
  }

  return await response.json();
};

export const remove = async (guid: string, access_token: string): Promise<BookmarkDeleteResponse> => {
  const response = await fetch(`${basePath}/delete`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${access_token}`
    },
    body: JSON.stringify({ guid })
  });

  if (!response.ok) {
    throw new Error("Failed to delete bookmark");
  }

  return await response.json();
};

export const find = async (data: BookmarksFindDataParam, access_token: string): Promise<BookmarksFindResponse> => {
  const response = await fetch(`${basePath}/find`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${access_token}`
    },
    body: JSON.stringify(data)
  });

  if (!response.ok) {
    throw new Error("Failed to find bookmarks");
  }

  return await response.json();
};

export const details = async (type: BookmarkType, id: string | number): Promise<BookmarksDetailsResponse> => {
  const params = new URLSearchParams({ bookmarkId: String(id), bookmarkType: type });
  const response = await fetch(`/ajax/bookmarks/fetch_details/?${params.toString()}`, {
    headers: {
      "Content-Type": "application/json"
    }
  });
  if (!response.ok) {
    throw new Error("Failed fetching bookmark details");
  }
  return await response.json();
};


type BookmarkDetailsJob = {
  jobId: number;
  state: string;
  title: string;
  url: string;
  salary: string;
  employer_image: string;
  employer_name: string;
  posted_date: string;
  type: string;
  location: string;
  postcode: string;
  renumeration_type: string;
  is_active: boolean;
  bs_product_id: string;
  bookmark_type: "job";
};
type BookmarkDetailsVolunteering = {
  jobId: number;
  state: string;
  title: string;
  url: string;
  salary: string;
  employer_image: string;
  employer_name: string;
  posted_date: string;
  type: string;
  location: string;
  postcode: string;
  renumeration_type: string;
  is_active: boolean;
  bs_product_id: string;
  bookmark_type: "volunteering";
};

type BookmarkDetailsArticle = {
  articleId: number;
  title: string;
  url: string;
  image: string;
  bs_product_id: string;
  bookmark_type: "article";
};


type BookmarkDetailsTraveldeal = {
  id: string;
  brandName: string;
  title: string;
  destinationFriendly: string;
  description: string;
  subtitle?: string;
  thumbnail: string;
  originalLink: string;
  link: string;
  priceWas?: string;
  priceNow?: string;
  priceUnformatted: number;
  priceType?: string;
  bs_product_id: string;
  traveldealId: string;
  bookmark_type: "traveldeal";
};

type BookmarkDetailsCourse = {
  courseId: number;
  title: string;
  url: string;
  educator_logo: string;
  educator_name: string;
  delivery_type: string;
  start_date: string;
  is_active: boolean;
  course_price_html: string;
  bs_product_id: string;
  bookmark_type: "course";
};
export type BookmarkDetails = BookmarkDetailsJob | BookmarkDetailsVolunteering | BookmarkDetailsArticle | BookmarkDetailsTraveldeal | BookmarkDetailsCourse;
export type BookmarksDetailsResponse = {
  results: BookmarkDetails[];
  paging: {
    pageOffset: number;
    pageSize: number;
    totalCount: number;
  };
  metadata: {
    jobTotal: number;
    volunteeringTotal: number;
    courseTotal: number;
    articleTotal: number;
    traveldealTotal: number;
  };
  status: string;
};

interface JobBookmark extends IBookmark {
  id: string;
  jobId: number;
  createdUtc: string;
  type: 'job';
};
interface VolunteeringBookmark extends IBookmark {
  id: string;
  volunteeringId: number;
  createdUtc: string;
  type: 'volunteering';
};
interface CourseBookmark extends IBookmark {
  id: string;
  courseId: number;
  createdUtc: string;
  type: 'course';
};
interface ArticleBookmark extends IBookmark {
  id: string;
  articleId: number;
  createdUtc: string;
  type: 'article';
};
interface TravelDealBookmark extends IBookmark {
  id: string;
  traveldealId: string;
  createdUtc: string;
  type: 'traveldeal';
};

interface IBookmark {
  type: BookmarkType;
  jobId?: number;
  volunteeringId?: number;
  courseId?: number;
  articleId?: number;
  traveldealId?: string;
};

export type Bookmark = JobBookmark | VolunteeringBookmark | CourseBookmark | ArticleBookmark | TravelDealBookmark;

export type BookmarksFindResponse = {
  status: "success" | "error";
  message: {
    results:  Bookmark[];
    paging: {
      pageOffset: number;
      pageSize: number;
      totalCount: number;
    };
    metadata: {
      jobTotal: number;
      volunteeringTotal: number;
      courseTotal: number;
      articleTotal: number;
      traveldealTotal: number;
    };
  }
};


type BookmarkAddResponse = {
  status: "success" | "error";
  dialog: string;
  id: string;
  jobId?: number;
  volunteerId?: number;
  courseId?: number;
  articleId?: number;
  travelDealId?: string;
  createdUtc: string;
};

type BookmarkDeleteResponse = {
  status: "success" | "error";
  guid: string;
};


export type BookmarksFindDataParam = {
  types: (BookmarkType)[];
  jobIds?: number[];
  volunteeringIds?: number[];
  articleIds?: number[];
  courseIds?: number[];
  traveldealIds?: string[];
};
